import React, { useMemo } from 'react';
import { gql } from '@soundxyz/gql-string';
import { GridCarousel } from '@components/main-components/carousel/GridCarousel';
import { useMediaQuery } from '@context/mediaQuery';
import {
  TrendingPostsCarouselDocument,
  type TrendingPostsCarouselQuery,
  useQuery,
} from '@graphql/reactQuery';
import { exhaustiveCheck } from '@utils/helpers';
import { Heading } from './Heading';
import { PlaylistPost } from './PlaylistPost';
import { ReleasePost } from './ReleasePost';
import { TrendingPostSkeleton } from './TrendingPostSkeleton';
import { customArrowClassName } from './shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  query TrendingPostsCarousel {
    trendingPostsPreview {
      id
      information {
        __typename
        ... on UserCreatedPostAggregate {
          post {
            id
            __typename
            ... on PlaylistPost {
              id
              ...TrendingPlaylistPost
            }
            ... on ReleasePost {
              id
              ...TrendingReleasePost
            }
          }
        }
      }
    }
  }
`);

const skeletonSlides = Array.from({ length: 5 }).map((_, i) => ({
  id: i.toString(),
  slide: <TrendingPostSkeleton key={`trending-post-skeleton-${i}`} />,
}));

export function TrendingPosts() {
  const { data, isInitialLoading: isLoading } = useQuery(TrendingPostsCarouselDocument, {
    staleTime: 0,
  });
  const { tablet } = useMediaQuery();

  const posts = useMemo(
    () => data?.data.trendingPostsPreview ?? [],
    [data?.data.trendingPostsPreview],
  );

  const postSlides = React.useMemo(
    () =>
      posts
        .filter(isUserCreatedPostAggregate)
        .map(postAggregate => {
          return {
            id: postAggregate.id,
            slide: <TrendingPost post={postAggregate.information.post} />,
          };
        })
        .filter(item => item.slide !== null),
    [posts],
  );

  const content = useMemo(() => {
    const hasEmptySlides = posts.length === 0;

    if (isLoading)
      return (
        <GridCarousel
          slides={skeletonSlides}
          onEndReached={null}
          onInternalActiveIndexChange={null}
          columnAmount={null}
          hideArrows={!tablet}
          externalActiveIndex={null}
          spacing={null}
          alignSlidesLeft={false}
          ssrSlideWidth={undefined}
          columnType="extra-large"
          arrowClassName={customArrowClassName}
        />
      );

    if (hasEmptySlides) {
      return null;
    }

    return (
      <div className="-mt-3 flex flex-col gap-y-4">
        <GridCarousel
          slides={postSlides}
          onEndReached={null}
          onInternalActiveIndexChange={null}
          columnAmount={null}
          hideArrows={!tablet}
          externalActiveIndex={null}
          spacing={null}
          alignSlidesLeft={false}
          ssrSlideWidth={undefined}
          columnType="extra-large"
          arrowClassName={customArrowClassName}
        />
      </div>
    );
  }, [isLoading, postSlides, posts, tablet]);

  return (
    <div className="w-full px-4 md:px-6">
      <Heading />
      {content}
    </div>
  );
}

type PostItem = TrendingPostsCarouselQuery['trendingPostsPreview'][number] & {
  information: { __typename: 'UserCreatedPostAggregate' };
};

function isUserCreatedPostAggregate(
  postItem: TrendingPostsCarouselQuery['trendingPostsPreview'][number],
): postItem is PostItem {
  return postItem.information.__typename === 'UserCreatedPostAggregate';
}

function TrendingPost({ post }: { post: PostItem['information']['post'] }) {
  if (!post) {
    return null;
  }

  switch (post.__typename) {
    case 'ReleasePost': {
      return <ReleasePost post={post} />;
    }
    case 'PlaylistPost': {
      return <PlaylistPost post={post} />;
    }
    case 'ActivityFeedGroupBackingPost':
    case 'ArtistDropPost':
    case 'Repost':
      return null;

    default:
      exhaustiveCheck(post);
  }

  return null;
}
